import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, BarChart3, ShieldCheck } from "lucide-react";
import { useEOSAuth } from "../../contexts/EOSAuthContext";

export type FormData = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

export const featureBullets = [
  {
    icon: BarChart3,
    title: "Live Market Monitoring",
    description: "Track positions and execution data across your portfolio in real time.",
  },
  {
    icon: ShieldCheck,
    title: "Secure Broker Connection",
    description: "Your credentials and trades stay protected end-to-end.",
  },
  {
    icon: Activity,
    title: "Automated Strategy Execution",
    description: "Let intelligent models act on your trading rules while you stay in control.",
  },
];

export function useLoginPage() {
  const { login, register, error, isLoading, isAuthenticated } = useEOSAuth();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/broker-selection", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      return;
    }

    try {
      setIsLoggingIn(true);
      await login({ email: formData.email, password: formData.password });
    } catch {
      // noop
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      return;
    }

    try {
      setIsRegistering(true);
      await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
    } catch {
      // noop
    } finally {
      setIsRegistering(false);
    }
  };

  const toggleForm = () => {
    setShowRegisterForm((prev) => !prev);
    setFormData({ email: "", password: "", firstName: "", lastName: "" });
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return {
    error,
    isLoading,
    isLoggingIn,
    isRegistering,
    showPassword,
    showRegisterForm,
    formData,
    handleInputChange,
    handleLogin,
    handleRegister,
    toggleForm,
    togglePasswordVisibility,
  };
}
